import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import BankLogo from './BankLogo';
import { resolveBankLogo } from '@/constants/pakistanBanks';

export interface BankAccountOption {
  name: string;
  bank_name: string;
  account_title?: string;
  logo?: string | null;
  is_default?: number | boolean;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  banks: BankAccountOption[];
  className?: string;
  placeholder?: string;
  required?: boolean;
  showAccountTitle?: boolean;
  size?: 'sm' | 'md';
}

export default function BankAccountSelect({
  value,
  onChange,
  banks,
  className = '',
  placeholder = 'Select bank',
  required = false,
  showAccountTitle = false,
  size = 'md',
}: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = banks.find((b) => b.name === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const triggerPad = size === 'sm' ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm';
  const optionPad = size === 'sm' ? 'px-2.5 py-1.5' : 'px-3 py-2';
  const logoSize =
    size === 'sm'
      ? {
          className: 'w-5 h-5 rounded-sm object-contain bg-white border border-gray-100 shrink-0',
          fallbackClassName:
            'w-5 h-5 rounded-sm bg-gray-100 border border-gray-200 flex items-center justify-center text-[9px] font-bold text-gray-500 shrink-0',
        }
      : {
          className: 'w-6 h-6 rounded-sm object-contain bg-white border border-gray-100 shrink-0',
          fallbackClassName:
            'w-6 h-6 rounded-sm bg-gray-100 border border-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-500 shrink-0',
        };

  const labelFor = (bank: BankAccountOption) => {
    if (
      showAccountTitle &&
      bank.account_title &&
      bank.account_title !== bank.bank_name
    ) {
      return `${bank.bank_name} — ${bank.account_title}`;
    }
    return bank.bank_name;
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between gap-2 border border-gray-200 rounded-lg bg-white font-medium text-left focus:outline-none focus:ring-1 focus:ring-primary-500 ${triggerPad}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 min-w-0">
          {selected ? (
            <>
              <BankLogo
                bankName={selected.bank_name}
                logoUrl={resolveBankLogo(selected.bank_name, selected.logo)}
                className={logoSize.className}
                fallbackClassName={logoSize.fallbackClassName}
              />
              <span className="truncate text-gray-900">{labelFor(selected)}</span>
            </>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Keep a hidden input so parent forms can still use required */}
      {required && (
        <input
          tabIndex={-1}
          className="sr-only"
          value={value}
          onChange={() => {}}
          required
        />
      )}

      {open && (
        <div
          className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto py-1"
          role="listbox"
        >
          {banks.length === 0 ? (
            <div className={`${optionPad} text-xs text-amber-600`}>No banks configured.</div>
          ) : (
            banks.map((bank) => {
              const isSelected = bank.name === value;
              return (
                <button
                  key={bank.name}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`w-full flex items-center gap-2 ${optionPad} text-left hover:bg-gray-50 transition-colors ${
                    isSelected ? 'bg-primary-50' : ''
                  }`}
                  onClick={() => {
                    onChange(bank.name);
                    setOpen(false);
                  }}
                >
                  <BankLogo
                    bankName={bank.bank_name}
                    logoUrl={resolveBankLogo(bank.bank_name, bank.logo)}
                    className={logoSize.className}
                    fallbackClassName={logoSize.fallbackClassName}
                  />
                  <span
                    className={`truncate ${
                      isSelected ? 'font-semibold text-primary-700' : 'font-medium text-gray-700'
                    } ${size === 'sm' ? 'text-xs' : 'text-sm'}`}
                  >
                    {labelFor(bank)}
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
